import { useEffect, useId, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

interface ResilienceIntelligenceOrbProps {
  size?: number;
  periodMs?: number;
  initialPaused?: boolean;
  /** When defined, takes precedence over internal state — the orb becomes controlled. */
  paused?: boolean;
  /**
   * One-cycle-per-trigger mode. Leave undefined and the orb loops forever
   * (the loading-spinner use). Supply a number and the orb plays exactly one
   * full cycle per increment, then parks on the exploded rest pose; bumping
   * the token mid-cycle buys one more whole period, so a run always ends on
   * a period boundary no matter how often the user re-triggers it.
   */
  cycleToken?: number;
  /** When false, renders just the SVG (no wrapper, no Pause/Play button). Defaults to true. */
  showButton?: boolean;
  /**
   * Where in the cycle a run begins, as a fraction of one period. Defaults to
   * 0 -- the exploded rest pose, which is also where every run ends.
   *
   * CUBE_CLOSED_PHASE starts a run on the shut cube instead, so the mark
   * arrives assembled and opens rather than sitting open and collapsing first.
   * On a sign-in screen that is the difference between an entrance and a
   * loading spinner: the last thing that happens is the cube blooming open,
   * not the orb being swallowed.
   *
   * Only the first run of a mount honours it. A re-trigger buys a whole
   * further period from wherever the orb parked, which is phase 0 -- the run
   * would otherwise jump backwards to the closed pose to start.
   */
  startPhase?: number;
  /**
   * Whether the orb and its glow dim as the cube closes.
   *
   * The cycle normally ramps the orb from full to 0.7 and the glow to 0.6 on
   * the way in, and back out again on the way open. On the sidebar that reads
   * as the light being occluded by the closing cube, which is the point. On a
   * sign-in screen the same ramp reads as the logo fading in and out, so it can
   * be held flat while the geometry still animates.
   */
  dim?: boolean;
  className?: string;
}
//
//
type Vec3 = [number, number, number];
type Quad = [number, number, number, number];

interface SideFace {
  idx: Quad;
  normal: Vec3;
  opacity: number;
}

const A = 30.06;
const B = 30.09;
const CX = 80.43;
const CY = 80.0;
const COS30 = Math.cos(Math.PI / 6);
const SIN30 = Math.sin(Math.PI / 6);

const CORNERS_3D: Vec3[] = [
  [A, -B, A], [A, -B, -A], [-A, -B, -A], [-A, -B, A],
  [A, B, A], [A, B, -A], [-A, B, -A], [-A, B, A],
];

const SIDE_FACES: SideFace[] = [
  { idx: [0, 1, 5, 4], normal: [1, 0, 0], opacity: 0.32 },
  { idx: [3, 0, 4, 7], normal: [0, 0, 1], opacity: 0.18 },
  { idx: [2, 3, 7, 6], normal: [-1, 0, 0], opacity: 0.32 },
  { idx: [1, 2, 6, 5], normal: [0, 0, -1], opacity: 0.18 },
];

const TOP_FACE_IDX: Quad = [4, 5, 6, 7];

const CAM: Vec3 = (() => {
  const v: Vec3 = [COS30, SIN30, COS30];
  const m = Math.hypot(v[0], v[1], v[2]);
  return [v[0] / m, v[1] / m, v[2] / m];
})();

const PANEL_GREY = '#d4d4d4';

function rotY(p: Vec3, theta: number): Vec3 {
  const c = Math.cos(theta);
  const s = Math.sin(theta);
  return [p[0] * c + p[2] * s, p[1], -p[0] * s + p[2] * c];
}

function rotNormY(n: Vec3, theta: number): Vec3 {
  const c = Math.cos(theta);
  const s = Math.sin(theta);
  return [n[0] * c + n[2] * s, n[1], -n[0] * s + n[2] * c];
}

function project(p: Vec3): [number, number] {
  return [CX + (p[0] - p[2]) * COS30, CY + (p[0] + p[2]) * SIN30 - p[1]];
}

function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

/** Wraps any phase into [0, 1). 1 would park a cycling orb before it moved. */
function normalisePhase(p: number): number {
  if (!Number.isFinite(p)) return 0;
  return ((p % 1) + 1) % 1;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

function lerpColor(c1: string, c2: string, t: number): string {
  const r1 = parseInt(c1.slice(1, 3), 16);
  const g1 = parseInt(c1.slice(3, 5), 16);
  const b1 = parseInt(c1.slice(5, 7), 16);
  const r2 = parseInt(c2.slice(1, 3), 16);
  const g2 = parseInt(c2.slice(3, 5), 16);
  const b2 = parseInt(c2.slice(5, 7), 16);
  const r = Math.round(lerp(r1, r2, t));
  const g = Math.round(lerp(g1, g2, t));
  const b = Math.round(lerp(b1, b2, t));
  return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
}

/**
 * The cube shut and still, a moment before the spin: the last frame of the
 * contraction and the first of the pre-spin hold. Named because it is a start
 * pose a caller can ask for, and because a bare 0.45 at the call site is a
 * number that stops meaning anything the moment the map below is retuned.
 */
export const CUBE_CLOSED_PHASE = 0.45;

// Phase boundaries (fractions of one full loop) — every animation curve below
// reads from the same `phase`, so they cannot drift apart:
//   0.000-0.150 hold exploded   0.450-0.480 brief hold pre-spin
//   0.150-0.450 contract        0.480-0.705 360° spin
//   0.705-0.780 brief hold      0.780-1.000 explode

function getExplodeAmount(p: number): number {
  if (p <= 0.15) return 1;
  if (p < 0.45) return 1 - easeInOut((p - 0.15) / 0.30);
  if (p <= 0.78) return 0;
  return easeInOut((p - 0.78) / 0.22);
}

function getRotationAngle(p: number): number {
  if (p <= 0.48) return 0;
  if (p >= 0.705) return Math.PI * 2;
  return easeInOut((p - 0.48) / 0.225) * Math.PI * 2;
}

function getOrbScale(p: number): number {
  if (p <= 0.15) return 1;
  if (p < 0.45) return lerp(1, 0.7, easeInOut((p - 0.15) / 0.30));
  if (p <= 0.78) return 0.7;
  return lerp(0.7, 1, easeInOut((p - 0.78) / 0.22));
}

function getOrbOpacity(p: number): number {
  if (p <= 0.15) return 1;
  if (p < 0.45) return lerp(1, 0.7, easeInOut((p - 0.15) / 0.30));
  if (p <= 0.78) return 0.7;
  return lerp(0.7, 1, easeInOut((p - 0.78) / 0.22));
}

function getGlowScale(p: number): number {
  if (p <= 0.15) return 1;
  if (p < 0.45) return lerp(1, 0.75, easeInOut((p - 0.15) / 0.30));
  if (p <= 0.78) return 0.75;
  return lerp(0.75, 1, easeInOut((p - 0.78) / 0.22));
}

function getGlowOpacity(p: number): number {
  if (p <= 0.15) return 1;
  if (p < 0.45) return lerp(1, 0.6, easeInOut((p - 0.15) / 0.30));
  if (p <= 0.78) return 0.6;
  return lerp(0.6, 1, easeInOut((p - 0.78) / 0.22));
}

function getOrbColor(p: number): string {
  const WARM = '#FFDC85';
  const COOL = '#D86820';
  if (p <= 0.15) return WARM;
  if (p < 0.45) return lerpColor(WARM, COOL, easeInOut((p - 0.15) / 0.30));
  if (p <= 0.78) return COOL;
  return lerpColor(COOL, WARM, easeInOut((p - 0.78) / 0.22));
}

function getGlowColor(p: number): string {
  const WARM = '#FFD27A';
  const COOL = '#E07830';
  if (p <= 0.15) return WARM;
  if (p < 0.45) return lerpColor(WARM, COOL, easeInOut((p - 0.15) / 0.30));
  if (p <= 0.78) return COOL;
  return lerpColor(COOL, WARM, easeInOut((p - 0.78) / 0.22));
}

function getBurstState(p: number): { opacity: number; radius: number } {
  if (p < 0.83 || p > 0.99) return { opacity: 0, radius: 18 };
  const t = (p - 0.83) / 0.16;
  let opacity: number;
  if (t < 0.25) opacity = lerp(0, 0.95, t / 0.25);
  else if (t < 0.6) opacity = lerp(0.95, 0.4, (t - 0.25) / 0.35);
  else opacity = lerp(0.4, 0, (t - 0.6) / 0.4);
  return { opacity, radius: lerp(22, 90, t) };
}

function buildPath(rotated: Vec3[], idx: Quad, shiftX: number, shiftY: number): string {
  const pts = idx.map(i => {
    const p = project(rotated[i]);
    return [p[0] + shiftX, p[1] + shiftY];
  });
  let d = `M${pts[0][0].toFixed(2)} ${pts[0][1].toFixed(2)}`;
  for (let k = 1; k < pts.length; k++) {
    d += ` L${pts[k][0].toFixed(2)} ${pts[k][1].toFixed(2)}`;
  }
  return `${d} Z`;
}

function centroid3D(rotated: Vec3[], idx: Quad): Vec3 {
  let x = 0;
  let y = 0;
  let z = 0;
  for (const i of idx) {
    x += rotated[i][0];
    y += rotated[i][1];
    z += rotated[i][2];
  }
  return [x / idx.length, y / idx.length, z / idx.length];
}

// RAF is suspended while the tab is hidden (and stalls under main-thread
// contention) but wall-clock time is not, so an unclamped frame delta would
// teleport the orb to an arbitrary phase on the next frame. Capping the delta
// costs a slow machine a little animation speed and buys a continuous curve.
const MAX_FRAME_MS = 100;

const ORB_TRANSFORM_STYLE: CSSProperties = {
  transformOrigin: '80.47px 85.58px',
  transformBox: 'view-box',
};
const GLOW_TRANSFORM_STYLE: CSSProperties = {
  transformOrigin: '80.43px 85.60px',
  transformBox: 'view-box',
};

export function ResilienceIntelligenceOrb({
  size = 320,
  periodMs = 4000,
  initialPaused = false,
  paused: controlledPaused,
  cycleToken,
  showButton = true,
  startPhase = 0,
  dim = true,
  className,
}: ResilienceIntelligenceOrbProps) {
  const reactId = useId();
  const uid = reactId.replace(/[^a-zA-Z0-9_-]/g, '');

  const svgRef = useRef<SVGSVGElement | null>(null);
  const faceTopRef = useRef<SVGPathElement | null>(null);
  const side1Ref = useRef<SVGPathElement | null>(null);
  const side2Ref = useRef<SVGPathElement | null>(null);
  const side3Ref = useRef<SVGPathElement | null>(null);
  const side4Ref = useRef<SVGPathElement | null>(null);
  const orbRef = useRef<SVGCircleElement | null>(null);
  const glowRef = useRef<SVGGElement | null>(null);
  const burstRef = useRef<SVGCircleElement | null>(null);
  const orbStopRef = useRef<SVGStopElement | null>(null);
  const glowStopRef = useRef<SVGStopElement | null>(null);

  const [internalPaused, setInternalPaused] = useState(initialPaused);
  const isControlled = controlledPaused !== undefined;
  const paused = isControlled ? controlledPaused : internalPaused;

  // One clock, and only one. `elapsed` is advanced solely by the RAF loop,
  // from measured frame deltas — never from wall-clock arithmetic, so a pause
  // or a hidden tab simply contributes no time. `target` is the elapsed value
  // the current run stops at and is always a whole number of periods, which
  // is what guarantees the final frame lands on phase 0 (the rest pose).
  // A start phase is an offset into the first run, not an extension of it:
  // the target stays at one whole period, so the run is the tail of a cycle
  // and still lands its final frame on phase 0.
  const startElapsed = normalisePhase(startPhase) * periodMs;
  const elapsedRef = useRef(startElapsed);
  const targetRef = useRef(periodMs);
  const lastFrameRef = useRef<number | null>(null);
  const appliedTokenRef = useRef<number | null>(null);
  const periodRef = useRef(periodMs);

  const filterId = `ri-filter-${uid}`;
  const burstGradId = `ri-burst-${uid}`;
  const glowGradId = `ri-glow-grad-${uid}`;
  const orbGradId = `ri-orb-grad-${uid}`;

  useEffect(() => {
    const sideEls = [side1Ref.current, side2Ref.current, side3Ref.current, side4Ref.current];
    const faceTop = faceTopRef.current;
    const svg = svgRef.current;
    const orb = orbRef.current;
    const glow = glowRef.current;
    const burst = burstRef.current;
    const orbStop = orbStopRef.current;
    const glowStop = glowStopRef.current;
    if (
      !svg || !faceTop || !orb || !glow || !burst || !orbStop || !glowStop ||
      sideEls.some(el => !el)
    ) {
      return;
    }

    const renderAt = (elapsed: number) => {
      const phase = (elapsed % periodMs) / periodMs;
      const angle = getRotationAngle(phase);
      const explode = getExplodeAmount(phase);
      const rotated = CORNERS_3D.map(c => rotY(c, angle));

      // PANELS
      const visibleSides: Array<{ face: SideFace; depthVal: number; screenX: number }> = [];
      for (const f of SIDE_FACES) {
        const n = rotNormY(f.normal, angle);
        if (dot(n, CAM) <= 0.001) continue;
        let sx = 0;
        let sy = 0;
        for (const i of f.idx) {
          const p = project(rotated[i]);
          sx += p[0];
          sy += p[1];
        }
        visibleSides.push({
          face: f,
          depthVal: dot(centroid3D(rotated, f.idx), CAM),
          screenX: sx / f.idx.length,
        });
      }
      const byX = [...visibleSides].sort((a, b) => a.screenX - b.screenX);
      const topShiftY = -9.5 * explode;

      for (let i = 0; i < 4; i++) {
        const el = sideEls[i]!;
        if (i < byX.length) {
          const s = byX[i];
          let shiftX = 0;
          const shiftY = 9.5 * explode;
          if (byX.length >= 2) {
            if (i === 0) shiftX = -12 * explode;
            else if (i === byX.length - 1) shiftX = 12 * explode;
          }
          el.setAttribute('d', buildPath(rotated, s.face.idx, shiftX, shiftY));
          el.setAttribute('fill', PANEL_GREY);
          el.setAttribute('fill-opacity', String(s.face.opacity));
        } else {
          el.setAttribute('d', '');
        }
      }

      const rendered = byX.map((s, i) => ({ ...s, el: sideEls[i]! }));
      rendered.sort((a, b) => a.depthVal - b.depthVal);
      const parent = sideEls[0]!.parentNode!;
      for (const r of rendered) parent.insertBefore(r.el, faceTop);
      faceTop.setAttribute('d', buildPath(rotated, TOP_FACE_IDX, 0, topShiftY));

      // ORB & GLOW
      orb.style.transform = `scale(${getOrbScale(phase)})`;
      orb.style.opacity = String(dim ? getOrbOpacity(phase) : 1);
      glow.style.transform = `scale(${getGlowScale(phase)})`;
      glow.style.opacity = String(dim ? getGlowOpacity(phase) : 1);

      // GRADIENT COLOURS
      orbStop.setAttribute('stop-color', getOrbColor(phase));
      glowStop.setAttribute('stop-color', getGlowColor(phase));

      // PINK BURST
      const burstState = getBurstState(phase);
      burst.setAttribute('opacity', String(burstState.opacity));
      burst.setAttribute('r', String(burstState.radius));
    };

    if (periodRef.current !== periodMs) {
      periodRef.current = periodMs;
      elapsedRef.current = startElapsed;
      targetRef.current = periodMs;
    }

    // A new token means "play one more full cycle". Extending the target by a
    // whole period rather than restarting the clock keeps the motion seamless
    // through a mid-cycle re-trigger and still ends the run on a boundary.
    const cycling = cycleToken !== undefined;
    if (cycling && appliedTokenRef.current !== cycleToken) {
      if (appliedTokenRef.current !== null) targetRef.current += periodMs;
      appliedTokenRef.current = cycleToken;
    }

    // Render the current frame synchronously so the dice are visible
    // immediately on mount instead of waiting one RAF tick — and so the SVG
    // is not blank while parked between hovers.
    renderAt(elapsedRef.current);

    const parked = cycling && elapsedRef.current >= targetRef.current;
    if (paused || parked) return;

    let rafId = 0;
    const loop = (now: number) => {
      const last = lastFrameRef.current;
      lastFrameRef.current = now;
      elapsedRef.current += last === null ? 0 : Math.min(now - last, MAX_FRAME_MS);

      if (cycling && elapsedRef.current >= targetRef.current) {
        // Land exactly on the boundary and stop: phase 0 is the rest pose.
        elapsedRef.current = 0;
        targetRef.current = 0;
        renderAt(0);
        return;
      }
      if (!cycling) elapsedRef.current %= periodMs;
      renderAt(elapsedRef.current);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafId);
      // The next run measures its own first delta from scratch, so whatever
      // time passes while we are not animating never reaches `elapsed`.
      lastFrameRef.current = null;
    };
  }, [paused, periodMs, cycleToken, dim, startElapsed]);

  const svgEl = (
    <svg
      ref={svgRef}
      width={size}
      height={size}
      viewBox="0 0 160 160"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ overflow: 'visible' }}
      className={showButton ? undefined : className}
    >
      <circle ref={burstRef} cx="80.4657" cy="85.578" r="20" fill={`url(#${burstGradId})`} opacity="0" />
      <g ref={glowRef} filter={`url(#${filterId})`} style={GLOW_TRANSFORM_STYLE}>
        <ellipse cx="80.4289" cy="85.6018" rx="32.8156" ry="32.6438" fill={`url(#${glowGradId})`} />
      </g>
      <circle
        ref={orbRef}
        cx="80.4657"
        cy="85.578"
        r="32.8602"
        fill={`url(#${orbGradId})`}
        style={ORB_TRANSFORM_STYLE}
      />

      <path ref={side1Ref} stroke={PANEL_GREY} strokeOpacity="0.6" strokeWidth="0.276899" />
      <path ref={side2Ref} stroke={PANEL_GREY} strokeOpacity="0.6" strokeWidth="0.276899" />
      <path ref={side3Ref} stroke={PANEL_GREY} strokeOpacity="0.6" strokeWidth="0.276899" />
      <path ref={side4Ref} stroke={PANEL_GREY} strokeOpacity="0.6" strokeWidth="0.276899" />
      <path
        ref={faceTopRef}
        fill={PANEL_GREY}
        fillOpacity="0.5"
        stroke={PANEL_GREY}
        strokeOpacity="0.6"
        strokeWidth="0.276899"
      />

      <defs>
        <filter
          id={filterId}
          x="11.8769"
          y="17.2216"
          width="137.102"
          height="136.76"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="17.8682" result="effect1_foregroundBlur" />
        </filter>

        <radialGradient id={burstGradId} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#FF4D8A" stopOpacity="0.9" />
          <stop offset="0.4" stopColor="#FF0468" stopOpacity="0.5" />
          <stop offset="1" stopColor="#FF0468" stopOpacity="0" />
        </radialGradient>

        <radialGradient
          id={glowGradId}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(80.4289 85.6018) rotate(90) scale(32.6438 32.8156)"
        >
          <stop ref={glowStopRef} offset="0" stopColor="#FFD27A" />
          <stop offset="1" stopColor="#FF0468" />
        </radialGradient>

        <radialGradient
          id={orbGradId}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(80.4657 85.578) rotate(90) scale(32.8602)"
        >
          <stop ref={orbStopRef} offset="0" stopColor="#FFDC85" />
          <stop offset="1" stopColor="#FF0468" />
        </radialGradient>
      </defs>
    </svg>
  );

  if (!showButton) {
    return svgEl;
  }

  return (
    <div
      className={className}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1.5rem',
        padding: '2rem 0',
      }}
    >
      {svgEl}
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button
          type="button"
          onClick={() => setInternalPaused(p => !p)}
          disabled={isControlled}
          style={{
            padding: '0.5rem 1rem',
            background: 'var(--color-background-secondary)',
            border: '0.5px solid var(--color-border-secondary)',
            borderRadius: 'var(--border-radius-md)',
            color: 'var(--color-text-primary)',
            fontFamily: 'var(--font-sans)',
            fontSize: 14,
            cursor: isControlled ? 'not-allowed' : 'pointer',
            opacity: isControlled ? 0.5 : 1,
          }}
        >
          {paused ? 'Play' : 'Pause'}
        </button>
      </div>
    </div>
  );
}

export default ResilienceIntelligenceOrb;
